import React, { useEffect, useState } from "react";
import http from "../../../../services/http";
import { useAdminPermissions } from "../../../hooks/useAdminPermissions";

/**
 * Chuyển chủ trạm — Quân chốt 14/10/2026 (ca Hanoi Sena Hotel #235: email dùng chung của nhân viên
 * đang là chủ; chủ muốn email riêng làm CHỦ, email chung vẫn dùng được nhưng chỉ là Lễ tân).
 *
 * Chỉ super admin thấy: khối gọi GET /admin/partners/:id/transfer-owner (gác partners.manage +
 * super admin ở máy chủ) — 403 thì không hiện gì. Máy chủ mới là nơi gác thật.
 */
type OldRole = "reception" | "accountant" | "none";

type Info = {
	partnerId: number;
	partnerName: string;
	ownerEmail: string;
	isChain: boolean;
	members: { role: string; email: string; status: string }[];
};

const NHAN_VAI: Record<OldRole, string> = {
	reception: "Reception (front desk)",
	accountant: "Accountant",
	none: "Remove access",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function TransferStationOwner(props: {
	partnerId: string;
	open: boolean;
	/** Gọi sau khi chuyển xong để form cha cập nhật ô email trạm (tránh Save gắn ngược chủ cũ). */
	onTransferred?: (newOwnerEmail: string) => void;
}) {
	const { hasPermission, loaded } = useAdminPermissions();
	const [info, setInfo] = useState<Info | null>(null);
	const [moForm, setMoForm] = useState(false);
	const [email, setEmail] = useState("");
	const [vaiCu, setVaiCu] = useState<OldRole>("reception");
	const [lyDo, setLyDo] = useState("");
	const [xacNhan, setXacNhan] = useState(false);
	const [busy, setBusy] = useState(false);
	const [loi, setLoi] = useState("");
	const [xong, setXong] = useState<{ toEmail: string; fromEmail: string; oldOwnerRole: OldRole; newOwnerCreated: boolean; emailSent: boolean } | null>(null);

	const tai = async () => {
		try {
			const { data } = await http.get(`/admin/partners/${props.partnerId}/transfer-owner`);
			setInfo(data?.data || null);
		} catch {
			setInfo(null); // 403 (không phải super admin) / 404 ⇒ ẩn khối
		}
	};

	useEffect(() => {
		setMoForm(false);
		setXacNhan(false);
		setXong(null);
		setLoi("");
		setEmail("");
		setLyDo("");
		setVaiCu("reception");
		if (props.open && props.partnerId && loaded && hasPermission("partners.manage")) tai();
		else setInfo(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.open, props.partnerId, loaded]);

	if (!info) return null;

	const emailMoi = email.trim().toLowerCase();
	const giuCho = (role: string) => info.members.find((m) => m.role === role && m.status !== "disabled");

	const kiemTruoc = (): string => {
		if (!EMAIL_RE.test(emailMoi)) return "Enter a valid email for the new owner.";
		if (emailMoi === info.ownerEmail) return "This is already the current owner email.";
		if (!lyDo.trim()) return "Please write a reason (kept in the audit log).";
		if (vaiCu !== "none") {
			const g = giuCho(vaiCu);
			if (g && g.email.toLowerCase() !== info.ownerEmail && g.email.toLowerCase() !== emailMoi) {
				return `The ${vaiCu === "reception" ? "Reception" : "Accountant"} role is already held by ${g.email}. Pick another option or change that member first.`;
			}
		}
		return "";
	};

	const moXacNhan = () => {
		const l = kiemTruoc();
		setLoi(l);
		if (!l) setXacNhan(true);
	};

	const chuyen = async () => {
		setBusy(true);
		setLoi("");
		try {
			const { data } = await http.post(`/admin/partners/${props.partnerId}/transfer-owner`, {
				newOwnerEmail: emailMoi,
				oldOwnerRole: vaiCu,
				reason: lyDo.trim(),
			});
			setXong(data?.data || null);
			if (data?.data?.toEmail) props.onTransferred?.(data.data.toEmail);
			setXacNhan(false);
			setMoForm(false);
			await tai();
		} catch (e: any) {
			setXacNhan(false);
			setLoi(e?.response?.data?.message || "Could not transfer the owner. Nothing was changed.");
		} finally {
			setBusy(false);
		}
	};

	const nut = (bg: string): React.CSSProperties => ({
		border: "none",
		borderRadius: 8,
		padding: "7px 14px",
		background: bg,
		color: "#fff",
		fontWeight: 700,
		fontSize: 13,
		cursor: busy ? "wait" : "pointer",
		opacity: busy ? 0.6 : 1,
	});
	const o: React.CSSProperties = {
		width: "100%",
		boxSizing: "border-box",
		padding: "7px 10px",
		border: "1px solid #D0D5DD",
		borderRadius: 8,
		fontSize: 13,
	};
	const nhan: React.CSSProperties = { display: "block", fontSize: 12.5, fontWeight: 600, color: "#344054", margin: "10px 0 4px" };

	return (
		<div
			data-testid="transfer-station-owner"
			style={{ border: "1px solid #D0D5DD", borderRadius: 10, padding: "14px 16px", marginBottom: 16, background: "#fff" }}>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
				<div style={{ fontWeight: 700, fontSize: 14, color: "#101828" }}>Transfer owner</div>
				<span style={{ background: "#F4EBFF", color: "#6941C6", borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
					Super admin
				</span>
			</div>
			<p style={{ margin: "0 0 8px", fontSize: 12.5, color: "#475467", lineHeight: 1.45 }}>
				Current owner: <b style={{ color: "#101828" }}>{info.ownerEmail || "—"}</b>
			</p>

			{xong ? (
				<div style={{ background: "#ECFDF3", border: "1px solid #ABEFC6", borderRadius: 8, padding: "10px 12px", fontSize: 13, color: "#067647", marginBottom: 8 }}>
					Owner transferred to <b>{xong.toEmail}</b>.{" "}
					{xong.oldOwnerRole === "none"
						? `${xong.fromEmail} no longer has access to this station.`
						: `${xong.fromEmail} is now ${NHAN_VAI[xong.oldOwnerRole]} and must sign in again.`}{" "}
					{xong.newOwnerCreated
						? xong.emailSent
							? "A temporary password was emailed to the new owner."
							: "The welcome email could not be sent — ask the new owner to use “Forgot password” on the station portal."
						: xong.emailSent
							? "The new owner was emailed and signs in with their existing password."
							: "The notification email could not be sent; the new owner signs in with their existing password."}
				</div>
			) : null}

			{info.isChain ? (
				<p style={{ margin: 0, fontSize: 12.5, color: "#B54708" }}>
					This station belongs to a chain. Transferring the owner of chain stations is not supported yet.
				</p>
			) : !moForm ? (
				<button type="button" style={nut("#475467")} onClick={() => { setMoForm(true); setXong(null); }}>
					Transfer owner…
				</button>
			) : (
				<div>
					<label style={nhan}>New owner email</label>
					<input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@example.com" style={o} />
					<label style={nhan}>What happens to the current owner account ({info.ownerEmail})</label>
					<select value={vaiCu} onChange={(e) => setVaiCu(e.target.value as OldRole)} style={o}>
						<option value="reception">{NHAN_VAI.reception} — keeps signing in, no owner rights</option>
						<option value="accountant">{NHAN_VAI.accountant} — keeps signing in, finance only</option>
						<option value="none">{NHAN_VAI.none} — can no longer open this station</option>
					</select>
					<label style={nhan}>Reason (kept in the audit log)</label>
					<textarea
						value={lyDo}
						onChange={(e) => setLyDo(e.target.value)}
						rows={2}
						placeholder="e.g. Owner asked to use their own email; shared account becomes front desk"
						style={{ ...o, resize: "vertical" }}
					/>
					<div style={{ display: "flex", gap: 8, marginTop: 10 }}>
						<button type="button" disabled={busy} style={nut("#B42318")} onClick={moXacNhan}>
							Review transfer
						</button>
						<button type="button" disabled={busy} style={nut("#98A2B3")} onClick={() => { setMoForm(false); setLoi(""); }}>
							Cancel
						</button>
					</div>
				</div>
			)}

			{xacNhan ? (
				<div
					role="dialog"
					aria-modal="true"
					style={{ position: "fixed", inset: 0, background: "rgba(16,24,40,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
					<div style={{ background: "#fff", borderRadius: 12, maxWidth: 480, width: "100%", padding: "18px 20px", boxShadow: "0 12px 32px rgba(16,24,40,0.25)" }}>
						<div style={{ fontWeight: 700, fontSize: 16, color: "#101828", marginBottom: 10 }}>Transfer owner of {info.partnerName || `#${info.partnerId}`}?</div>
						<ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 13, color: "#344054", lineHeight: 1.6 }}>
							<li>
								<b>{emailMoi}</b> becomes the OWNER (full rights, payouts, signing, team).
							</li>
							<li>
								If <b>{emailMoi}</b> has no WhaleLO account yet, one is created and a temporary password is emailed to it.
							</li>
							<li>
								<b>{info.ownerEmail}</b>{" "}
								{vaiCu === "none"
									? "loses access to this station."
									: `stays able to sign in as ${NHAN_VAI[vaiCu]} — no owner rights.`}{" "}
								All its open sessions are signed out.
							</li>
							<li>Bank details, payouts, balance, orders and signed documents are NOT changed.</li>
						</ul>
						<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
							<button type="button" disabled={busy} style={nut("#98A2B3")} onClick={() => setXacNhan(false)}>
								Back
							</button>
							<button type="button" disabled={busy} style={nut("#B42318")} onClick={chuyen}>
								{busy ? "Transferring…" : "Transfer owner"}
							</button>
						</div>
					</div>
				</div>
			) : null}

			{loi ? <p style={{ margin: "8px 0 0", color: "#B42318", fontSize: 12.5 }}>{loi}</p> : null}
		</div>
	);
}
